import React, { useState, useEffect, useRef } from 'react';
import Table from './Table';
import InfoPage from './InfoPage';

// Funktion zum Starten der Konfetti-Animation über einem bestimmten HTML-Container.
// Sie erstellt ein Canvas-Element, zeichnet Konfetti-Stücke und animiert deren Fall.
export function launchConfettiOverBox(canvas) {
  // Überprüft, ob das Canvas-Element existiert.
  if (!canvas) {
    console.error("Confetti canvas element is null.");
    return;
  }

  const ctx = canvas.getContext("2d");
  const container = canvas.parentNode; // Der übergeordnete Knoten ist der Quiz-Container.
  // Überprüft, ob der übergeordnete Container existiert.
  if (!container) {
    console.error("Confetti canvas has no parent container.");
    return;
  }

  const width = container.clientWidth; // Breite des Containers.
  const height = container.offsetHeight; // Höhe des Containers.
  canvas.width = width;
  canvas.height = height;
  canvas.style.display = "block";
  canvas.style.position = "absolute"; // Absolut positioniert innerhalb des Quiz-Containers.
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.pointerEvents = "none"; // Lässt Klicks auf darunterliegende Elemente zu.
  canvas.style.zIndex = "1000"; // Stellt sicher, dass das Konfetti über anderen Elementen liegt.

  // Initialisiert 100 Konfetti-Stücke mit zufälligen Startpositionen, Größen, Farben und Bewegungen.
  const pieces = Array.from({ length: 100 }, () => ({
    x: Math.random() * width, // Zufällige X-Startposition.
    y: height - 10 + Math.random() * 10, // Startet knapp über dem unteren Rand.
    r: Math.random() * 6 + 2, // Radius des Konfetti-Stücks.
    color: ["#fad7d2", "#fce8dc", "#d6a77a", "#ebf2f7", "#8ecae6", "#ffb703", "#ff8fa3", "#caffbf"][Math.floor(Math.random() * 8)], // Zufällige Farbe aus definierter Palette.
    speed: Math.random() * 0.5 + 0.3, // Zufällige Fallgeschwindigkeit.
    drift: Math.random() * 2 - 1, // Zufällige seitliche Drift.
    alpha: 1 // Anfangs-Deckkraft.
  }));

  let frame = 0; // Korrekte Deklaration der Frame-Variable.
  const duration = 120; // Dauer der Animation in Frames (ca. 2 Sekunden bei 60fps).

  // Die Haupt-Zeichenfunktion, die in jedem Animationsframe aufgerufen wird.
  function draw() {
    ctx.clearRect(0, 0, width, height); // Löscht den Canvas-Inhalt des vorherigen Frames.
    pieces.forEach(p => {
      const fadeStart = duration * 0.6; // Zeitpunkt in Frames, ab dem das Konfetti ausblendet.
      if (frame > fadeStart) {
        // Reduziert die Deckkraft, um ein Ausblenden zu simulieren.
        p.alpha = Math.max(0, 1 - (frame - fadeStart) / (duration - fadeStart));
      }

      ctx.beginPath();
      // Zeichnet einen Kreis für jedes Konfetti-Stück.
      ctx.arc(p.x + frame * p.drift, p.y - frame * p.speed, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.alpha; // Wendet die aktuelle Deckkraft an.
      ctx.fill();
      ctx.globalAlpha = 1; // Setzt die globale Deckkraft für nachfolgende Zeichnungen zurück.
    });

    frame++; // 'frame' inkrementieren.
    if (frame < duration) {
      requestAnimationFrame(draw); // Fordert den nächsten Animationsframe an.
    } else {
      canvas.style.display = "none"; // Blendet das Canvas aus, wenn die Animation beendet ist.
    }
  }

  draw(); // Startet die Konfetti-Animation.
}


// Quiz-Komponente zur Anzeige von Fragen und Bearbeitung von Antworten.
// React.memo wird verwendet, um unnötige Neu-Renderings zu vermeiden,
// wenn sich die Props nicht ändern.
const Quiz = React.memo(({ quizData }) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null); // Speichert den Index der aktuell ausgewählten Antwort.
  const [feedbackMessage, setFeedbackMessage] = useState(''); // Speichert die Feedback-Nachricht (Richtig/Falsch).
  const [isCorrect, setIsCorrect] = useState(false); // Verfolgt, ob die richtige Antwort gefunden wurde.
  const [attemptedWrongIndexes, setAttemptedWrongIndexes] = useState([]); // Speichert die Indizes der falsch versuchten Antworten.
  const quizContainerRef = useRef(null); // Referenz auf das DOM-Element des Quiz-Containers.
  const canvasRef = useRef(null); // Referenz auf das DOM-Element des Canvas.

  // Setzt den Quiz-Zustand zurück, wenn sich die Quiz-Daten (z.B. bei Kapitelwechsel) ändern.
  useEffect(() => {
    setSelectedOptionIndex(null);
    setFeedbackMessage('');
    setIsCorrect(false);
    setAttemptedWrongIndexes([]); // Setzt die Liste der falschen Versuche zurück.
  }, [quizData]); // Abhängigkeit von quizData stellt sicher, dass der Reset beim Kapitelwechsel erfolgt.

  // Startet Konfetti, wenn die Antwort korrekt ist und das Canvas-Element verfügbar ist.
  useEffect(() => {
    if (isCorrect && canvasRef.current) { // Prüft, ob isCorrect true ist UND Canvas-Element existiert.
      launchConfettiOverBox(canvasRef.current); // Canvas-Element direkt übergeben.
    }
  }, [isCorrect]); // Dieser useEffect reagiert nur auf Änderungen in isCorrect.

  // Behandelt das Klicken auf eine Antwortoption.
  const handleOptionClick = (index) => {
    if (isCorrect) return; // Wenn die Antwort bereits korrekt ist, verhindere weitere Klicks.
    setSelectedOptionIndex(index);
    setFeedbackMessage(''); // Löscht vorheriges Feedback, wenn eine neue Option ausgewählt wird.
  };

  // Behandelt das Absenden der Antwort.
  const handleSubmit = () => {
    if (selectedOptionIndex === null) {
      setFeedbackMessage('Bitte wähle eine Antwort aus.');
      return;
    }

    if (selectedOptionIndex === quizData.correctAnswerIndex) {
      setFeedbackMessage('Richtig! Gut gemacht!');
      setIsCorrect(true); // Setzt den Zustand auf korrekt, was den useEffect oben triggert.
    } else {
      setFeedbackMessage('Falsch. Bitte versuche es noch einmal.');
      // Fügt die aktuell ausgewählte (falsche) Option zur Liste der falschen Versuche hinzu.
      setAttemptedWrongIndexes(prev => [...prev, selectedOptionIndex]);
      setSelectedOptionIndex(null); // Löscht die Auswahl für den nächsten Versuch.
    }
  };

  return (
    <div ref={quizContainerRef} className="bg-[#Faf6f4] p-6 rounded-lg shadow-inner mt-4 relative overflow-hidden border border-gray-200">
      <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full pointer-events-none"></canvas>
      <h4 className="text-xl text-gray-800 mb-4 font-open-sans font-normal">{quizData.question}</h4> {/* Wendet die Open Sans-Schriftart auf die Frage an. */}
      <div className="space-y-3">
        {quizData.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(index)}
            className={`w-full text-left p-3 rounded-md border-2 transition-colors duration-300 font-open-sans font-normal
              ${selectedOptionIndex === index && !isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'} /* Styling für ausgewählte Antwort (vor dem Absenden). */
              ${isCorrect && index === quizData.correctAnswerIndex ? 'bg-green-100 border-green-500 ring-4 ring-green-300' : ''} /* Styling für die korrekte Antwort nach dem Absenden. */
              ${!isCorrect && attemptedWrongIndexes.includes(index) ? 'bg-red-100 border-red-500' : ''} /* Styling für falsch versuchte Antworten. */
              ${isCorrect ? 'cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'} /* Cursor und Hover-Effekt. */
            `}
            disabled={isCorrect} // Deaktiviert alle Buttons, sobald die richtige Antwort gefunden wurde.
          >
            {option}
          </button>
        ))}
      </div>
      {!isCorrect && ( // Zeigt den Absende-Button nur an, wenn die Antwort noch nicht korrekt ist.
        <button
          onClick={handleSubmit}
          className="mt-6 px-6 py-3 rounded-full transition-colors shadow-md font-open-sans bg-[#008080] text-white hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50"
        >
          Antwort absenden
        </button>
      )}
      {feedbackMessage && ( // Zeigt die Feedback-Nachricht an.
        <p className={`mt-4 text-lg font-open-sans ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
          {feedbackMessage}
        </p>
      )}
    </div>
  );
}); // Ende von React.memo() für die Quiz-Komponente.

const VIDEO_CROP = {
  scale: 1,
  translateY: '0%'
};

const PLACEHOLDER_SRCS = [
  '/images/placeholders/1 - marie moechte irgendwann ausziehen.webp',
  '/images/placeholders/2 - miete wohnen ohne eigentum.webp',
  '/images/placeholders/3 - die zweite miete.webp',
  '/images/placeholders/4 - kaufen statt mieten.webp',
  '/images/placeholders/5 - eigentumswohnung und gemeinschaft.webp',
  '/images/placeholders/6 - geld fuer ein haus.webp',
  '/images/placeholders/7 - der energieausweis.webp',
  '/images/placeholders/8 - sanieren und modernisieren.webp',
  '/images/placeholders/9 - notar und grundbuch.webp',
  '/images/placeholders/10 - ein plan fuer die zukunft.webp'
];

const placeholderPreloadCache = [];

const preloadPlaceholders = () => {
  if (placeholderPreloadCache.length > 0) {
    return;
  }

  PLACEHOLDER_SRCS.forEach((src) => {
    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    image.src = src;
    placeholderPreloadCache.push(image);
  });
};

const getPlaceholderSrc = (src) => {
  if (!src || !src.startsWith('/images/') || !src.endsWith('.png')) {
    return null;
  }

  return src.replace('/images/', '/images/placeholders/').replace(/\.png$/, '.webp');
};

const AnimatedMedia = ({ block }) => {
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [mediaAspectRatio, setMediaAspectRatio] = useState(block.aspectRatio || null);
  const placeholderSrc = block.placeholderSrc || getPlaceholderSrc(block.src);

  useEffect(() => {
    setIsVideoVisible(false);
    setHasVideoError(false);
    setMediaAspectRatio(block.aspectRatio || null);
  }, [block.src, block.videoSrc, block.aspectRatio]);

  if (!block.videoSrc) {
    const sizeClass = block.size || 'max-w-full h-auto max-h-80 object-contain';
    return (
      <div className="flex justify-center my-6 px-4">
        <img
          src={block.src}
          alt={block.alt}
          className={sizeClass}
          onError={(e) => { e.target.onerror = null; e.target.src = '/images/icon.png'; }}
        />
      </div>
    );
  }

  return (
    <div className="flex justify-center my-6 px-4">
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white"
        style={{ aspectRatio: mediaAspectRatio || '1 / 1' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {placeholderSrc && (
          <img
            src={placeholderSrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
            decoding="sync"
            fetchPriority="high"
            loading="eager"
          />
        )}
        <img
          src={block.src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 z-10 h-full w-full"
          style={{
            objectFit: 'cover',
            transform: `translateY(${block.cropTranslateY || VIDEO_CROP.translateY}) scale(${block.cropScale || VIDEO_CROP.scale})`,
            transformOrigin: 'center center'
          }}
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.target;
            if (naturalWidth && naturalHeight) {
              setMediaAspectRatio(`${naturalWidth} / ${naturalHeight}`);
            }
          }}
          onError={(e) => { e.target.onerror = null; e.target.src = '/images/icon.png'; }}
        />
        {!hasVideoError && (
          <video
            src={block.videoSrc}
            className={`absolute inset-0 z-20 h-full w-full transition-opacity duration-300 ${isVideoVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              objectFit: 'cover',
              transform: `translateY(${block.cropTranslateY || VIDEO_CROP.translateY}) scale(${block.cropScale || VIDEO_CROP.scale})`,
              transformOrigin: 'center center'
            }}
            autoPlay
            muted
            playsInline
            loop={Boolean(block.loop)}
            preload="auto"
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            aria-label={block.alt}
            onContextMenu={(e) => e.preventDefault()}
            onLoadedData={() => setIsVideoVisible(true)}
            onPlaying={() => setIsVideoVisible(true)}
            onEnded={() => setIsVideoVisible(true)}
            onError={() => setHasVideoError(true)}
          />
        )}
      </div>
    </div>
  );
};

// Hauptkomponente der Anwendung, die die Geschichte und Navigation verwaltet.
const App = () => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0); // Zustand für den aktuellen Kapitelindex.
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Zustand für die Sichtbarkeit des Hamburger-Menüs.
  const [isInfoOpen, setIsInfoOpen] = useState(false); // Zustand für die Sichtbarkeit der Info-Seite.
  const mainContentRef = useRef(null);

  useEffect(() => {
    preloadPlaceholders();
  }, []);

  // Scrollt beim Kapitelwechsel automatisch zum Seitenanfang und schließt das Menü.
  useEffect(() => {
    // Scrolle den <main>-Container nach oben
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    // Scrolle zusätzlich das Hauptfenster, was auf Mobilgeräten hilft
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  }, [currentChapterIndex]); // Dieser Effekt wird bei jeder Änderung von currentChapterIndex ausgelöst.

  // Definition der gesamten Geschichte "Marie und ihr Haus" mit Kapiteln und deren Inhalten.
  const marieStory = {
    id: 'marie-haus',
    title: 'Marie und ihr Haus',
    description: 'Eine Geschichte über Wohnen, Eigentum und wichtige Entscheidungen rund um Immobilien.',
    chapters: [
      {
        title: 'Marie und ihr Haus',
        isCoverPage: true,
        hideFromContents: true,
        content: [
          {
            type: 'image',
            src: '/images/2 - miete wohnen ohne eigentum.png',
            videoSrc: '/animations/2.mp4',
            aspectRatio: '4 / 3',
            loop: true,
            size: 'max-w-full h-auto max-h-full object-contain',
            alt: 'Titelbild von Marie'
          }
        ]
      },
      {
        title: '1. Marie möchte irgendwann ausziehen',
        content: [
          {
            type: 'image',
            src: '/images/1 - marie moechte irgendwann ausziehen.png',
            videoSrc: '/animations/1.mp4',
            aspectRatio: '3 / 4',
            loop: true,
            alt: 'Zeichnung 2'
          },
          {
            type: 'paragraph',
            text: 'Marie ist 18 und lebt noch bei ihren Eltern. Doch irgendwann möchte sie ausziehen und später mit ihrem Freund Paul zusammenwohnen. Ihr Traum ist ein eigenes Haus mit Garten – so wie das Haus ihrer Eltern. Aber ein Haus ist teuer. Vielleicht müssen Marie und Paul zuerst eine Wohnung mieten oder später eine Eigentumswohnung kaufen. Jede dieser Möglichkeiten hat Vorteile und Nachteile.'
          },
          {
            type: 'paragraph',
            text: 'Marie schaut sich die Häuser in ihrer Straße an. „Eigentlich verrückt“, denkt sie. „Alle wohnen irgendwo – aber ich weiß noch nicht genau, wie das alles funktioniert.“'
          },
          {
            type: 'quiz',
            question: 'Was sind Immobilien?',
            options: [
              'Ein Mobile an der Decke, das nicht mehr funktioniert.',
              'Grundstücke, Häuser und Wohnungen.',
              'Ein Auto, das nicht mehr fährt.'
            ],
            correctAnswerIndex: 1
          }
        ]
      },
      {
        title: '2. Miete – wohnen ohne Eigentum',
        content: [
          {
            type: 'image',
            src: '/images/2 - miete wohnen ohne eigentum.png',
            videoSrc: '/animations/2.mp4',
            aspectRatio: '4 / 3',
            loop: true,
            alt: 'Zeichnung 7'
          },
          {
            type: 'paragraph',
            text: 'Viele Menschen wohnen zur Miete. Die Wohnung gehört dann dem Vermieter. Der Mieter zahlt jeden Monat Geld dafür, dort wohnen zu dürfen. Der Vorteil: Man bleibt flexibel und kann leichter umziehen. Außerdem kümmert sich der Vermieter meist um größere Reparaturen am Gebäude.'
          },
          {
            type: 'paragraph',
            text: 'Marie denkt: „Das ist praktisch. Aber die Wohnung gehört einem dann eben nicht.“'
          },
          {
            type: 'quiz',
            question: 'Was bedeutet es, eine Wohnung zu mieten?',
            options: [
              'Man kauft die Wohnung und wird Eigentümer.',
              'Man darf in der Wohnung wohnen und zahlt dafür regelmäßig Geld an den Vermieter.',
              'Man bekommt die Wohnung geschenkt.'
            ],
            correctAnswerIndex: 1
          }
        ]
      },
      {
        title: '3. Die „zweite Miete“',
        content: [
          {
            type: 'image',
            src: '/images/3 - die zweite miete.png',
            videoSrc: '/animations/3.mp4',
            aspectRatio: '4 / 3',
            alt: 'Zeichnung 5'
          },
          {
            type: 'paragraph',
            text: 'Neben der Miete gibt es noch zusätzliche Kosten. Diese nennt man Betriebskosten. Dazu gehören zum Beispiel Heizung, Wasser, Müllabfuhr oder die Reinigung des Treppenhauses. Viele Menschen nennen diese Kosten auch die zweite Miete, weil sie jeden Monat zusätzlich bezahlt werden müssen.'
          },
          {
            type: 'paragraph',
            text: 'Marie merkt: „Beim Wohnen muss man immer auf mehrere Kosten gleichzeitig achten.“'
          },
          {
            type: 'quiz',
            question: 'Was gehört nicht zu den Betriebskosten?',
            options: [
              'Die Heizkosten.',
              'Die Kosten für die Müllabfuhr.',
              'Die Kosten für die Hausverwaltung.'
            ],
            correctAnswerIndex: 2
          }
        ]
      },
      {
        title: '4. Kaufen statt mieten',
        content: [
          {
            type: 'image',
            src: '/images/4 - kaufen statt mieten.png',
            videoSrc: '/animations/4.mp4',
            aspectRatio: '4 / 3',
            alt: 'Zeichnung 13'
          },
          {
            type: 'paragraph',
            text: 'Wer eine Wohnung oder ein Haus kauft, wird Eigentümer. Das bedeutet: Die Immobilie gehört einem selbst. Das gibt Freiheit – aber auch Verantwortung. Reparaturen, Versicherungen oder Renovierungen müssen selbst organisiert und bezahlt werden.'
          },
          {
            type: 'paragraph',
            text: 'Paul sagt: „Eigentum ist schön. Aber alles, was kaputtgeht, gehört dann auch dir.“'
          },
          {
            type: 'quiz',
            question: 'Wer ist der Eigentümer einer Wohnung?',
            options: [
              'Die Person, die die Wohnung gemietet hat.',
              'Der Hausmeister.',
              'Die Person, die im Grundbuch als Besitzer eingetragen ist.'
            ],
            correctAnswerIndex: 2
          }
        ]
      },
      {
        title: '5. Eigentumswohnung und Gemeinschaft',
        content: [
          {
            type: 'image',
            src: '/images/5 - eigentumswohnung und gemeinschaft.png',
            videoSrc: '/animations/5.mp4',
            aspectRatio: '4 / 3',
            alt: 'Zeichnung 15 (Plan)'
          },
          {
            type: 'paragraph',
            text: 'In einem Mehrfamilienhaus gehört einem meist nur die eigene Wohnung. Teile wie Dach, Treppenhaus oder Garten gehören allen Eigentümern gemeinsam. Diese Bereiche nennt man Gemeinschaftseigentum. Deshalb müssen viele Entscheidungen zusammen getroffen werden.'
          },
          {
            type: 'paragraph',
            text: 'Marie denkt: „Man wohnt also nicht nur neben anderen Menschen – man entscheidet auch mit ihnen.“'
          },
          {
            type: 'quiz',
            question: 'Was gehört zum Gemeinschaftseigentum in einem Mehrfamilienhaus?',
            options: [
              'Die Küche in einer Wohnung.',
              'Das Treppenhaus.',
              'Das Sofa im Wohnzimmer.'
            ],
            correctAnswerIndex: 1
          }
        ]
      },
      {
        title: '6. Geld für ein Haus',
        content: [
          {
            type: 'image',
            src: '/images/6 - geld fuer ein haus.png',
            videoSrc: '/animations/6.mp4',
            aspectRatio: '4 / 3',
            alt: 'Zeichnung 11'
          },
          {
            type: 'paragraph',
            text: 'Wohnungen und Häuser sind teuer. Die meisten Menschen können sie nicht vollständig aus eigenen Ersparnissen bezahlen. Deshalb nehmen viele Käufer einen Immobilienkredit bei einer Bank auf. Dieses Geld wird über viele Jahre zurückgezahlt – meistens jeden Monat ein Stück. Dazu kommen Zinsen, die man für das geliehene Geld bezahlen muss.'
          },
          {
            type: 'paragraph',
            text: 'Marie staunt: „Ein Haus bezahlt man also oft über viele Jahre.“'
          },
          {
            type: 'quiz',
            question: 'Was sind Zinsen bei einem Kredit?',
            options: [
              'Geld, das man zusätzlich zum geliehenen Betrag an die Bank bezahlt.',
              'Geld, das nur einmal am Anfang bezahlt wird.',
              'Geld, das man nur zahlt, wenn man umzieht.'
            ],
            correctAnswerIndex: 0
          }
        ]
      },
      {
        title: '7. Der Energieausweis',
        content: [
          {
            type: 'image',
            src: '/images/7 - der energieausweis.png',
            videoSrc: '/animations/7.mp4',
            aspectRatio: '3 / 4',
            alt: 'Zeichnung 3'
          },
          {
            type: 'paragraph',
            text: 'Beim Kauf oder bei der Vermietung einer Immobilie spielt auch der Energieverbrauch eine Rolle. Dafür gibt es den Energieausweis. Er zeigt, wie energieeffizient ein Gebäude ist. Die Skala reicht meist von A+ (sehr effizient) bis H (weniger effizient).'
          },
          {
            type: 'paragraph',
            text: 'Marie schaut auf die Skala und denkt: „Das ist ein bisschen wie eine Schulnote für ein Haus.“'
          },
          {
            type: 'quiz',
            question: 'Wozu dient der Energieausweis bei einem Haus oder einer Wohnung?',
            options: [
              'Er zeigt, wie viel Energie ein Haus braucht.',
              'Er zeigt, wie hoch die Miete ist.',
              'Er zeigt, wie viele Menschen dort wohnen.'
            ],
            correctAnswerIndex: 0
          }
        ]
      },
      {
        title: '8. Sanieren und modernisieren',
        content: [
          {
            type: 'image',
            src: '/images/8 - sanieren und modernisieren.png',
            videoSrc: '/animations/8.mp4',
            aspectRatio: '3 / 4',
            alt: 'Zeichnung 15 (Altbau)'
          },
          {
            type: 'paragraph',
            text: 'Viele Häuser sind schon älter. Dann müssen sie saniert oder modernisiert werden. Dabei werden zum Beispiel Fenster ausgetauscht, Dächer erneuert oder Heizungen verbessert. Solche Maßnahmen können teuer sein, helfen aber oft dabei, Energie zu sparen und das Gebäude zu erhalten.'
          },
          {
            type: 'paragraph',
            text: 'Paul sagt: „Ein Haus bleibt nur schön, wenn man sich regelmäßig darum kümmert.“'
          },
          {
            type: 'quiz',
            question: 'Was bedeutet es, ein Haus zu sanieren?',
            options: [
              'Ein Haus zu reparieren oder zu verbessern.',
              'Ein Haus neu zu bauen oder zu planen.',
              'Ein Haus zu vermieten oder zu verkaufen.'
            ],
            correctAnswerIndex: 0
          }
        ]
      },
      {
        title: '9. Notar und Grundbuch',
        content: [
          {
            type: 'image',
            src: '/images/9 - notar und grundbuch.png',
            videoSrc: '/animations/9.mp4',
            aspectRatio: '4 / 3',
            alt: 'Zeichnung 9'
          },
          {
            type: 'paragraph',
            text: 'Wenn sich Käufer und Verkäufer über eine Immobilie einig sind, wird ein Kaufvertrag vorbereitet. In Deutschland muss dieser Vertrag von einem Notar beurkundet werden. Der Notar liest den Vertrag vor, erklärt wichtige Punkte und sorgt dafür, dass alles rechtlich korrekt abläuft.'
          },
          {
            type: 'paragraph',
            text: 'Außerdem kümmert er sich darum, dass der neue Eigentümer im Grundbuch eingetragen wird. Dort steht genau, wem eine Immobilie gehört.'
          },
          {
            type: 'quiz',
            question: 'Was ist ein Grundbuch?',
            options: [
              'Ein Buch, das mit Grund beschmiert ist.',
              'Ein Buch darüber, was man auf bestimmten Böden pflanzen kann.',
              'Ein öffentliches Register mit Infos zu Eigentümern und Schulden bei Immobilien.'
            ],
            correctAnswerIndex: 2
          }
        ]
      },
      {
        title: '10. Ein Plan für die Zukunft',
        content: [
          {
            type: 'image',
            src: '/images/10 - ein plan fuer die zukunft.png',
            videoSrc: '/animations/10.mp4',
            aspectRatio: '4 / 3',
            alt: 'Zeichnung 18'
          },
          {
            type: 'paragraph',
            text: 'Marie und Paul haben inzwischen viel gelernt – über Miete, Eigentum, Kredite, Energieverbrauch, Sanierungen und Verträge. Marie sagt nachdenklich: „Ganz schön viele Dinge, die man bedenken muss.“ Paul nickt. „Ein Haus kauft man nicht einfach so.“'
          },
          {
            type: 'paragraph',
            text: 'Die beiden beschließen, sich Zeit zu nehmen, alles gründlich zu überlegen – und gemeinsam einen guten Plan für ihre Zukunft zu schmieden.'
          },
          {
            type: 'quiz',
            question: 'Was beschließen Marie und Paul am Ende?',
            options: [
              'Sofort ein Haus zu kaufen.',
              'Sich Zeit zu nehmen und alles gut zu planen.',
              'Nie wieder über Geld oder Häuser nachzudenken.'
            ],
            correctAnswerIndex: 1
          }
        ]
      },
      {
        "title": "Impressum",
        "hideFromContents": true,
        "content": [
          { "type": "paragraph", "text": "<strong>Angaben gemäß § 5 DDG</strong>" },
          { "type": "paragraph", "text": "Rosa-Maria Krämer<br />Wöllstädter Str. 4<br />60385 Frankfurt" },
          { "type": "paragraph", "text": "<strong>Kontakt:</strong>" },
          { "type": "paragraph", "text": "Telefon: <a href='tel:+496994599849' class='text-[#008080] hover:text-[#006666] underline'>0049 (0)69 94- 59 98 49</a><br />E-Mail: <a href='mailto:RosaKra@t-online.de' class='text-[#008080] hover:text-[#006666] underline'>RosaKra@t-online.de</a>" },
          { "type": "paragraph", "text": "<strong>Inhaltlich verantwortlich gemäß § 18 Abs. 2 MStV</strong>" },
          { "type": "paragraph", "text": "Rosa-Maria Krämer, Wöllstädter Str. 4, 60385 Frankfurt" },
          { "type": "paragraph", "text": "<strong>Haftungsausschluss:</strong>" },
          { "type": "paragraph", "text": "<strong>Haftung für Inhalte</strong><br />Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen." },
          { "type": "paragraph", "text": "<strong>Haftung für Links</strong><br />Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen." },
          { "type": "paragraph", "text": "<strong>Datenschutz</strong>" },
          { "type": "paragraph", "text": "Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten möglich. Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder eMail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis. Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben." },
          { "type": "paragraph", "text": "Wir weisen darauf hin, dass die Datenübertragung im Internet (z.B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich." },
          { "type": "paragraph", "text": "Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten durch Dritte zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit ausdrücklich widersprochen. Die Betreiber der Seiten behalten sich ausdrücklich rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-Mails, vor." },
          { "type": "paragraph", "text": "<strong>Text:</strong> Rosa-Maria Krämer" },
          { "type": "paragraph", "text": "<strong>Illustrationen & Entwicklung:</strong> H.O.P.E. – humans on planet earth UG" }
        ]
      }
    ],
  };


  const currentChapter = marieStory.chapters[currentChapterIndex];
  const isFirstChapter = currentChapterIndex === 0;
  const isLastChapter = marieStory.chapters.length - 1 === currentChapterIndex;

  useEffect(() => {
    const handleKeyDown = (event) => {
      const targetTagName = event.target?.tagName;
      const isFormField = targetTagName === 'INPUT' || targetTagName === 'TEXTAREA' || event.target?.isContentEditable;

      if (isFormField) {
        return;
      }

      if (event.key === 'ArrowLeft' && !isFirstChapter) {
        event.preventDefault();
        setCurrentChapterIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      }

      if (event.key === 'ArrowRight' && !isLastChapter) {
        event.preventDefault();
        setCurrentChapterIndex((prevIndex) => Math.min(prevIndex + 1, marieStory.chapters.length - 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFirstChapter, isLastChapter, marieStory.chapters.length]);

  // Handler für das vorherige Kapitel.
  const handlePreviousChapter = () => {
    if (!isFirstChapter) {
      setCurrentChapterIndex(currentChapterIndex - 1);
    }
  };

  // Handler für das nächste Kapitel.
  const handleNextChapter = () => {
    if (!isLastChapter) {
      setCurrentChapterIndex(currentChapterIndex + 1);
    }
  };

  // Handler für die Auswahl eines Kapitels aus dem Menü.
  const handleChapterSelect = (index) => {
    setCurrentChapterIndex(index);
    setIsMenuOpen(false); // Menü schließen nach Auswahl.
  };

  return (
    // Äußerer Container, der das "Telefon"-Layout auf großen Bildschirmen erstellt.
    <div className="flex justify-center items-center bg-gray-100 md:bg-white md:p-8">
      {/* Simulierter Notch/Kamera-Bereich für das Telefon-Layout */}
      <div className="relative w-full h-full md:w-[414px] md:h-[896px] md:rounded-[3rem] md:shadow-2xl md:border-[10px] md:border-gray-800 md:overflow-hidden bg-white flex flex-col">
        {isInfoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 md:absolute md:p-6"
            onClick={() => setIsInfoOpen(false)}
          >
            <InfoPage onClose={() => setIsInfoOpen(false)} />
          </div>
        )}

        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap" rel="stylesheet" />
        <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-xl z-50"></div>
        <header className="relative pt-8 pb-4 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`fixed md:absolute top-4 left-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008080] z-50 ${isMenuOpen || isInfoOpen ? 'hidden' : ''}`}
            aria-label="Menü öffnen"
          >
            <svg className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <button
            onClick={() => setIsInfoOpen(!isInfoOpen)}
            className={`fixed md:absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008080] z-50 ${isMenuOpen || isInfoOpen ? 'hidden' : ''}`}
            aria-label="Info öffnen"
          >
            <svg className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </header>

        {/* Sidebar-Menü für die Kapitelauswahl. */}
        <div className={`fixed inset-y-0 left-0 w-full bg-white shadow-lg transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out z-40 flex flex-col md:absolute md:rounded-[3rem]`}>
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800 font-open-sans">Inhalt</h3>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#008080]"
              aria-label="Menü schließen"
            >
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {marieStory.chapters.map((chapter, index) => {
                if (chapter.hideFromContents) {
                  return null;
                }
                return (
                  <li key={index}>
                    <button
                      onClick={() => handleChapterSelect(index)}
                      className={`block w-full text-left p-2 font-open-sans text-lg transition-colors ${index === currentChapterIndex ? 'text-[#008080] font-bold bg-green-50' : 'text-gray-700 hover:text-[#008080] hover:bg-gray-50'}`}
                    >
                      {chapter.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        {isMenuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:absolute md:rounded-[3rem]" onClick={() => setIsMenuOpen(false)}></div>
        )}

        <main ref={mainContentRef} className="flex-grow overflow-y-auto px-10 sm:px-6 lg:px-10 pb-20 flex flex-col">
          <h2 className="text-2xl sm:text-3xl font-normal text-gray-800 text-left pt-8 pb-3">
            {currentChapter.title}
          </h2>
          <div className="flex-grow space-y-4 text-base text-gray-700 leading-relaxed font-open-sans font-normal">
            {currentChapter.content.map((block, index) => {
              const key = `${currentChapterIndex}-${index}`;
              if (block.type === 'paragraph') {
                return <p key={index} dangerouslySetInnerHTML={{ __html: block.text }}></p>;
              } else if (block.type === 'image') {
                return <AnimatedMedia key={key} block={block} />;
              } else if (block.type === 'image-large') {
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className="max-w-full h-auto max-h-full object-contain"
                      onError={(e) => { e.target.onerror = null; e.target.src = '/images/icon.png'; }}
                    />
                  </div>
                );
              } else if (block.type === 'animation') {
                const sizeClass = block.size || 'max-w-full h-auto max-h-80 object-contain';
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className={sizeClass}
                    />
                  </div>
                );
              } else if (block.type === 'animation-large') {
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className="max-w-full h-auto max-h-full object-contain"
                      onError={(e) => { e.target.onerror = null; e.target.src = '/images/icon.png'; }}
                    />
                  </div>
                );
              } else if (block.type === 'link') {
                return (
                  <span key={key}>
                    <a href={block.href} target="_blank" rel="noopener noreferrer" className="text-[#008080] hover:underline font-semibold">
                      {block.text}
                    </a>
                    {' '}
                  </span>
                );
              } else if (block.type === 'customTable') {
                return <Table key={index} data={block.data} />;
              } else if (block.type === 'quiz') {
                return <Quiz key={index} quizData={block} />;
              }
              return null;
            })}
          </div>
          <footer className="text-center pt-8">
            <button
              onClick={() => handleChapterSelect(marieStory.chapters.length - 1)}
              className="text-[#008080] hover:text-[#006666] hover:underline font-open-sans"
            >
              Impressum
            </button>
          </footer>
        </main>

        <div className="fixed md:absolute bottom-0 left-0 right-0 p-4 flex justify-between items-center z-20 pointer-events-none">
          <button
            onClick={handlePreviousChapter}
            disabled={isFirstChapter}
            className={`p-3 rounded-full transition-colors bg-[#008080] text-white pointer-events-auto ${isFirstChapter ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50'}`}
            aria-label="Vorheriges Kapitel"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNextChapter}
            disabled={isLastChapter}
            className={`p-3 rounded-full transition-colors bg-[#008080] text-white pointer-events-auto ${isLastChapter ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50'}`}
            aria-label="Nächstes Kapitel"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
